import { useState, useRef, useEffect, useMemo } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { MEDIA_FOLDERS, type MediaResponse, type MediaFolder } from "@ckgo2/shared/types";
import { formatFileSize } from "@/lib/mediaFormat";
import AlbumTree from "@/components/media/AlbumTree";
import UploadZone from "@/components/media/UploadZone";
import MoveFolderModal from "@/components/media/MoveFolderModal";
import AddToAlbumModal from "@/components/media/AddToAlbumModal";
import MediaDetailDrawer from "@/components/media/MediaDetailDrawer";
import DryRunActionModal from "@/components/media/DryRunActionModal";
import {
  Upload,
  Trash2,
  Loader2,
  FolderOpen,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Move,
  Layers,
  Folder,
} from "lucide-react";

interface ListResponse {
  data: MediaResponse[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface MergePreviewGroup {
  hash: string;
  canonicalId: string;
  canonicalAlt: string;
  dupIds: string[];
}
interface MergeResult {
  groups: number;
  duplicates: number;
  entitiesTouched: number;
  preview: MergePreviewGroup[];
}
interface AutoOrganizeResult {
  albumsCreated: number;
  itemsAdded: number;
}

export default function MediaPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"active" | "trash">("active");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [folder, setFolder] = useState<MediaFolder | "">("");
  const [albumId, setAlbumId] = useState<string | null>(null);
  const [addToAlbumOpen, setAddToAlbumOpen] = useState(false);
  const [autoOrganizeOpen, setAutoOrganizeOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedRef = useRef<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const includeTrashed = tab === "trash";

  const listKey = ["media-files", { includeTrashed, folder, albumId, q: debouncedSearch }] as const;

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: listKey,
      queryFn: ({ pageParam }) => {
        const params = new URLSearchParams({ limit: "60" });
        if (pageParam) params.set("cursor", pageParam);
        if (folder) params.set("folder", folder);
        if (albumId) params.set("folderId", albumId);
        if (debouncedSearch) params.set("q", debouncedSearch);
        if (includeTrashed) params.set("includeTrashed", "true");
        return api.get<ApiResponse<ListResponse>>(`/api/admin/upload?${params}`);
      },
      initialPageParam: "",
      getNextPageParam: (lastPage) => lastPage.data?.nextCursor ?? undefined,
    });

  const items: MediaResponse[] = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.data?.data ?? []),
    [data],
  );

  // Reset selection when switching tabs/filters
  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab, folder, albumId, debouncedSearch]);

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ["media-files"] });
  const invalidateAlbums = () => queryClient.invalidateQueries({ queryKey: ["media-folders"] });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) =>
      ids.length === 1
        ? api.delete(`/api/admin/upload/${ids[0]}`)
        : api.post("/api/admin/upload/bulk-delete", { ids }),
    onSuccess: () => {
      invalidateList();
      setSelectedIds(new Set());
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map((id) => api.post(`/api/admin/upload/${id}/restore`, {}))),
    onSuccess: () => {
      invalidateList();
      setSelectedIds(new Set());
    },
  });

  const moveMutation = useMutation({
    mutationFn: ({ ids, folder: target }: { ids: string[]; folder: MediaFolder }) =>
      api.post("/api/admin/upload/bulk-move", { ids, folder: target }),
    onSuccess: () => {
      invalidateList();
      setSelectedIds(new Set());
      setMoveOpen(false);
    },
  });

  const handleCardClick = (id: string, e: React.MouseEvent) => {
    // shift = range select, cmd/ctrl = toggle, plain = open detail
    if (e.shiftKey && lastClickedRef.current) {
      const ids = items.map((m) => m.id);
      const a = ids.indexOf(lastClickedRef.current);
      const b = ids.indexOf(id);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        });
      }
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      lastClickedRef.current = id;
      return;
    }
    setDetailId(id);
  };

  if (error) {
    return (
      <div className="card text-error-600 text-center">Error loading media. Please try again.</div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Media library</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage all images</p>
        </div>
        <div className="flex gap-2 self-start">
          <button onClick={() => setMergeOpen(true)} className="btn-secondary">
            <Layers className="h-5 w-5" />
            Find duplicates
          </button>
          <button onClick={() => setUploadOpen((v) => !v)} className="btn-primary">
            <Upload className="h-5 w-5" />
            Upload
            {uploadOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Collapsible upload zone */}
      {uploadOpen && <UploadZone onUploaded={invalidateList} />}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {(["active", "trash"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`border-b-2 px-4 py-2 text-sm font-medium capitalize transition-colors ${
              tab === t
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "trash" ? "Trash" : "Active"}
          </button>
        ))}
      </div>

      {/* Albums sidebar + main content */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <aside className="shrink-0 space-y-2 sm:w-56">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Albums</h2>
            <button
              type="button"
              onClick={() => setAutoOrganizeOpen(true)}
              className="btn-ghost text-xs"
              title="Auto-organize albums by destination"
            >
              <Layers className="h-3.5 w-3.5" /> Auto
            </button>
          </div>
          <AlbumTree manage selectedId={albumId} onSelect={setAlbumId} />
        </aside>

        <div className="flex-1 space-y-4">
          {/* Toolbar: search + folder filter */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search alt, tags, filename, title…"
                className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800"
              />
            </div>
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value as MediaFolder | "")}
              className="rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              <option value="">All folders</option>
              {MEDIA_FOLDERS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="bg-primary-50 dark:bg-primary-900/20 flex flex-wrap items-center gap-2 rounded-lg p-2">
              <span className="text-primary-700 dark:text-primary-300 px-2 text-sm font-medium">
                {selectedIds.size} selected
              </span>
              {tab === "active" ? (
                <>
                  <button onClick={() => setMoveOpen(true)} className="btn-ghost text-sm">
                    <Move className="h-4 w-4" /> Move to folder…
                  </button>
                  <button onClick={() => setAddToAlbumOpen(true)} className="btn-ghost text-sm">
                    <Folder className="h-4 w-4" /> Add to album…
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete ${selectedIds.size} item(s)?`))
                        bulkDeleteMutation.mutate(Array.from(selectedIds));
                    }}
                    className="btn-ghost text-error-600 hover:bg-error-50 text-sm"
                  >
                    <Trash2 className="h-4 w-4" /> Soft delete
                  </button>
                </>
              ) : (
                <button
                  onClick={() => restoreMutation.mutate(Array.from(selectedIds))}
                  className="btn-ghost text-sm"
                >
                  <RotateCcw className="h-4 w-4" /> Restore
                </button>
              )}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="btn-ghost ml-auto text-sm"
              >
                <X className="h-4 w-4" /> Clear
              </button>
            </div>
          )}

          {/* Grid */}
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <FolderOpen className="mb-4 h-16 w-16 text-gray-300" />
              <p className="text-lg font-medium">
                {tab === "trash" ? "Trash is empty" : "No media"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {items.map((m) => {
                const sel = selectedIds.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={(e) => handleCardClick(m.id, e)}
                    title={m.alt || m.originalName || m.id}
                    className={`group relative overflow-hidden rounded-lg border-2 text-left transition-all ${
                      sel
                        ? "border-primary-500 ring-primary-500/30 ring-2"
                        : detailId === m.id
                          ? "border-primary-400"
                          : "border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                    }`}
                  >
                    <div className="aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800">
                      <img
                        src={m.thumbUrl}
                        alt={m.alt}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="p-1.5">
                      <p className="truncate text-xs font-medium text-gray-900 dark:text-white">
                        {m.alt || m.originalName || m.id}
                      </p>
                      <p className="truncate text-[11px] text-gray-500">
                        {m.folder} · {formatFileSize(m.sizeBytes)}
                      </p>
                    </div>
                    {sel && (
                      <div className="bg-primary-500 absolute left-1.5 top-1.5 h-4 w-4 rounded-full ring-2 ring-white" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {hasNextPage && (
            <div className="flex justify-center">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="btn-secondary"
              >
                {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load more"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {detailId && (
        <MediaDetailDrawer
          id={detailId}
          inTrash={tab === "trash"}
          onClose={() => setDetailId(null)}
          onChanged={invalidateList}
        />
      )}

      {/* Move-to-folder modal */}
      {moveOpen && (
        <MoveFolderModal
          count={selectedIds.size}
          onClose={() => setMoveOpen(false)}
          onMove={(target) => moveMutation.mutate({ ids: Array.from(selectedIds), folder: target })}
          pending={moveMutation.isPending}
        />
      )}

      {/* Merge-duplicates modal (dry-run → apply) */}
      {mergeOpen && (
        <DryRunActionModal<MergeResult>
          title="Merge duplicate images"
          endpoint="/api/admin/backfill-media/merge-duplicates"
          dryQueryKey={["media-merge-dryrun"]}
          canApply={(r) => !!r && r.groups > 0}
          applyLabel={(r) => `Merge ${r?.duplicates ?? 0} duplicate(s)`}
          renderPreview={(r, loading) =>
            loading ? (
              <div className="flex h-40 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : !r || r.groups === 0 ? (
              <p className="py-10 text-center text-sm text-gray-500">
                No exact duplicates found. (Duplicates are detected by SHA-256 content hash — run
                the content backfill first if hashes are missing.)
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Found <strong>{r.groups}</strong> duplicate group(s) ·{" "}
                  <strong>{r.duplicates}</strong> image(s) will be soft-deleted ·{" "}
                  <strong>{r.entitiesTouched}</strong> reference(s) repointed to the canonical
                  image.
                </p>
                <ul className="space-y-2">
                  {r.preview.map((g) => (
                    <li
                      key={g.hash}
                      className="rounded-lg border border-gray-200 p-2 text-xs dark:border-gray-700"
                    >
                      <p className="font-medium text-gray-900 dark:text-white">
                        Keep: {g.canonicalAlt || g.canonicalId}
                      </p>
                      <p className="text-gray-500">
                        Removing {g.dupIds.length} duplicate(s): {g.dupIds.join(", ")}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )
          }
          onClose={() => setMergeOpen(false)}
          onDone={() => {
            invalidateList();
            setMergeOpen(false);
          }}
        />
      )}

      {/* Add-to-album modal */}
      {addToAlbumOpen && (
        <AddToAlbumModal
          mediaIds={Array.from(selectedIds)}
          onClose={() => setAddToAlbumOpen(false)}
          onDone={() => {
            setAddToAlbumOpen(false);
            setSelectedIds(new Set());
            invalidateAlbums();
          }}
        />
      )}

      {/* Auto-organize modal (dry-run → apply) */}
      {autoOrganizeOpen && (
        <DryRunActionModal<AutoOrganizeResult>
          title="Auto-organize albums"
          endpoint="/api/admin/media-folders/auto-organize"
          dryQueryKey={["media-auto-organize-dryrun"]}
          canApply={() => true}
          applyLabel={() => "Organize"}
          renderPreview={(r, loading) =>
            loading ? (
              <div className="flex h-24 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This will create <strong>{r?.albumsCreated ?? 0}</strong> album(s) mirroring the
                destination hierarchy and file <strong>{r?.itemsAdded ?? 0}</strong> image(s) into
                them. Existing albums and memberships are kept.
              </p>
            )
          }
          onClose={() => setAutoOrganizeOpen(false)}
          onDone={() => {
            setAutoOrganizeOpen(false);
            invalidateAlbums();
          }}
        />
      )}
    </div>
  );
}
